import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Headphones, Sunrise, Users, Edit3, Moon,
  TrendingUp, DollarSign, Play, Pause, Clock, ChevronRight,
  Sparkles, Crown, Coins, Compass, Flame
} from 'lucide-react';
import EdgeRitualPlayer from './EdgeRitualPlayer';
import { EDGE_PHRASES } from './edgeRitualPhrases';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── THE EDGE — Performance Rituals (hardcoded, real audio served from
//      /api/audio/voice/edge/). These render at the top of the library
//      as the headline "Guided Performance" category for leaders.
//
// Order is intentional: Identity FIRST. Pre-Call lives in /networker/
// resets but appears here as a deep-link reminder for the spec'd
// "Daily Identity → Pre-Call → ..." learning order.
const EDGE_RITUALS = [
  {
    id: 'edge_daily_money_identity',
    name: 'Daily Money Identity',
    description: 'Anchor your identity. Loops your affirmations.',
    duration_minutes: 3,
    duration_seconds: 47, // intro length; loop continues seamlessly
    icon: 'crown',
    color: 'from-amber-500 to-rose-600',
    audio_url: '/api/audio/voice/edge/ritual_daily_money_identity_intro.m4a',
    loop_audio_url: '/api/audio/voice/edge/ritual_daily_money_identity_loop.m4a',
    is_hybrid: true,
    sections: [
      { type: 'system intro' },
      { type: 'identity loop' },
    ],
  },
  {
    id: 'edge_instant_manifestation',
    name: 'Instant Manifestation — Leader Reality',
    description: 'Step into the version of you already leading',
    duration_minutes: 4,
    duration_seconds: 197,
    icon: 'sparkles',
    color: 'from-amber-500 to-orange-600',
    audio_url: '/api/audio/voice/edge/ritual_instant_manifestation.m4a',
    sections: [
      { type: 'arrival' },
      { type: 'visualisation' },
      { type: 'identity' },
      { type: 'integration' },
    ],
  },
  {
    id: 'edge_future_team',
    name: 'Future Team Visualisation',
    description: 'See your organisation alive and self-leading',
    duration_minutes: 2,
    duration_seconds: 119,
    icon: 'users',
    color: 'from-amber-400 to-yellow-600',
    audio_url: '/api/audio/voice/edge/ritual_future_team.m4a',
    sections: [
      { type: 'arrival' },
      { type: 'visualisation' },
      { type: 'integration' },
    ],
  },
  {
    id: 'edge_wealth_normalisation',
    name: 'Wealth Normalisation',
    description: 'Soften into bigger numbers as your new baseline',
    duration_minutes: 2,
    duration_seconds: 94,
    icon: 'coins',
    color: 'from-amber-600 to-amber-800',
    audio_url: '/api/audio/voice/edge/ritual_wealth_normalisation.m4a',
    sections: [
      { type: 'arrival' },
      { type: 'visualisation' },
      { type: 'integration' },
    ],
  },
  {
    id: 'edge_night_integration',
    name: 'Night Integration — The Edge',
    description: 'Coming soon — let the day integrate while you sleep',
    duration_minutes: 4,
    duration_seconds: 0,
    icon: 'moon',
    color: 'from-indigo-700 to-slate-900',
    audio_url: null,
    coming_soon: true,
    sections: [{ type: 'evening integration' }],
  },
];

const NetworkerAudioLibraryPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const loopAudioRef = useRef(null);
  const [introEnded, setIntroEnded] = useState(false);
  const [autoplayedFromUrl, setAutoplayedFromUrl] = useState(false);

  const iconMap = {
    sunrise: Sunrise,
    users: Users,
    'edit-3': Edit3,
    moon: Moon,
    'trending-up': TrendingUp,
    'dollar-sign': DollarSign,
    headphones: Headphones,
    sparkles: Sparkles,
    crown: Crown,
    coins: Coins,
    compass: Compass,
    flame: Flame,
  };

  const categoryLabels = {
    edge: 'Performance Rituals',
    morning: 'Morning',
    preparation: 'Preparation',
    creativity: 'Creativity',
    evening: 'Evening',
    abundance: 'Abundance',
    sales: 'Sales',
  };

  useEffect(() => {
    fetchLibrary();
  }, []);

  // Deep-link auto-launch: /networker/audio-library?play=edge_*
  // selects the session and triggers Begin Session in one beat.
  useEffect(() => {
    if (autoplayedFromUrl || loading) return;
    const playId = searchParams.get('play');
    if (!playId) return;
    const target = sessions.find((s) => s.id === playId);
    if (!target) return;
    setAutoplayedFromUrl(true);
    setSelectedSession(target);
    // Defer one tick so the detail view mounts before we set isPlaying.
    setTimeout(() => setIsPlaying(true), 30);
    // Strip the param so a refresh inside the player doesn't re-trigger.
    const next = new URLSearchParams(searchParams);
    next.delete('play');
    setSearchParams(next, { replace: true });
  }, [loading, sessions, searchParams, setSearchParams, autoplayedFromUrl]);

  const fetchLibrary = async () => {
    try {
      const token = localStorage.getItem('reclaim_token');
      const res = await fetch(`${API_URL}/api/networker/audio-library`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      // Prepend Edge rituals as their own category — server data still loads underneath.
      const merged = { edge: EDGE_RITUALS, ...(data.by_category || {}) };
      setSessions([...EDGE_RITUALS, ...(data.sessions || [])]);
      setCategories(merged);
    } catch (err) {
      console.error('Error fetching library:', err);
      // Even if the server endpoint fails, at least show Edge rituals.
      setCategories({ edge: EDGE_RITUALS });
      setSessions(EDGE_RITUALS);
    } finally {
      setLoading(false);
    }
  };

  // ─── Edge ritual playback (real audio via <audio> elements). When the
  //     intro ends on a hybrid track (Daily Money Identity Activation), we
  //     immediately start the looping affirmation track underneath so the
  //     listener flows from system voice straight into their own identity.
  //
  //     For edge_* rituals we now hand off to <EdgeRitualPlayer/> which
  //     drives its own audio + cinematic synced phrase overlay. The
  //     legacy <audio> refs below are kept for non-edge fallback only.
  const startEdgeAudio = () => {
    if (!selectedSession?.audio_url) return;
    setIntroEnded(false);
    // Edge rituals are rendered by <EdgeRitualPlayer/>, which owns its
    // own <audio> elements. We just flip isPlaying so the overlay
    // mounts and the user gestures (this click) authorise audio.
    if (selectedSession.id?.startsWith('edge_')) {
      setIsPlaying(true);
      return;
    }
    if (audioRef.current) {
      audioRef.current.src = `${API_URL}${selectedSession.audio_url}`;
      audioRef.current.play().catch((e) => console.error('audio play err', e));
    }
    setIsPlaying(true);
  };

  const handleIntroEnded = () => {
    setIntroEnded(true);
    if (selectedSession?.is_hybrid && selectedSession.loop_audio_url && loopAudioRef.current) {
      loopAudioRef.current.src = `${API_URL}${selectedSession.loop_audio_url}`;
      loopAudioRef.current.loop = true;
      loopAudioRef.current.play().catch((e) => console.error('loop play err', e));
    }
  };

  const stopEdgeAudio = () => {
    if (audioRef.current) audioRef.current.pause();
    if (loopAudioRef.current) loopAudioRef.current.pause();
    setIsPlaying(false);
    setIntroEnded(false);
  };

  const startSession = () => {
    if (selectedSession?.audio_url) {
      startEdgeAudio();
      return;
    }
    setIsPlaying(true); // legacy server-driven path (no audio file yet)
  };

  const completeSession = async () => {
    if (!selectedSession) return;
    stopEdgeAudio();
    if (selectedSession.id?.startsWith('edge_')) {
      setSelectedSession(null);
      return;
    }
    try {
      const token = localStorage.getItem('reclaim_token');
      await fetch(`${API_URL}/api/networker/audio-library/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: selectedSession.id,
          duration_seconds: (selectedSession.duration_minutes || 0) * 60,
        }),
      });
      setSelectedSession(null);
    } catch (err) {
      console.error('Error completing session:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Deep-link cinematic loading screen — when /networker/audio-library?play=edge_*
  // is hit, we never want the user to see the library list/category grid
  // flash on screen before the player mounts. Render a black-amber
  // bridge until selectedSession+isPlaying flip on.
  const pendingDeepLink = searchParams.get('play') ||
    (autoplayedFromUrl && (!selectedSession || !isPlaying));
  if (pendingDeepLink) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center"
        data-testid="edge-deeplink-loader"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, #1A1410 0%, #0E0B08 55%, #050402 100%)",
        }}
      >
        <div className="text-center">
          <div className="mx-auto w-3 h-3 rounded-full"
               style={{
                 background: "rgba(217,165,90,0.85)",
                 boxShadow: "0 0 22px rgba(217,165,90,0.55)",
                 animation: "edgePulse 1.6s ease-in-out infinite",
               }} />
          <p className="mt-5 text-[10px] uppercase tracking-[0.5em] text-amber-500/60">
            The Edge
          </p>
        </div>
        <style>{`@keyframes edgePulse { 0%,100% { transform: scale(1); opacity: .6 } 50% { transform: scale(1.6); opacity: 1 } }`}</style>
      </div>
    );
  }

  // Session detail / playback view
  if (selectedSession) {
    const Icon = iconMap[selectedSession.icon] || Headphones;

    // Edge rituals get the full cinematic synced overlay.
    if (isPlaying && selectedSession.id?.startsWith('edge_')) {
      const phrases = EDGE_PHRASES[selectedSession.id] || [];
      const loopPhrases = selectedSession.is_hybrid
        ? (EDGE_PHRASES[`${selectedSession.id}_loop`] || [])
        : [];
      // Per-ritual cinematic underscore (low-volume background music).
      // These are Suno tracks uploaded by Michael — longer, richer,
      // purpose-fit for each ritual's emotional arc. Previously we had
      // placeholder `meditation.mp3` (identical to `nature.mp3`) and
      // short `cinematic.mp3` — replaced with the Suno library below.
      //
      //   Instant Manifestation  → glass_morning       (gentle dawn,
      //                                                 imaginal state)
      //   Future Team            → open_horizon_drift  (expansive)
      //   Wealth Normalisation   → riverglass_harmony  (flow)
      //   Daily Money Identity   → soft_return_loop    (loop-friendly)
      const BG_MUSIC = {
        edge_instant_manifestation: '/api/audio/ambient/glass_morning.mp3',
        edge_future_team:           '/api/audio/ambient/open_horizon_drift.mp3',
        edge_wealth_normalisation:  '/api/audio/ambient/riverglass_harmony.mp3',
        edge_daily_money_identity:  '/api/audio/ambient/soft_return_loop.mp3',
      };
      const bgMusicUrl = BG_MUSIC[selectedSession.id]
        ? `${API_URL}${BG_MUSIC[selectedSession.id]}`
        : null;
      return (
        <EdgeRitualPlayer
          audioUrl={`${API_URL}${selectedSession.audio_url}`}
          loopAudioUrl={
            selectedSession.is_hybrid && selectedSession.loop_audio_url
              ? `${API_URL}${selectedSession.loop_audio_url}`
              : null
          }
          bgMusicUrl={bgMusicUrl}
          bgMusicVolume={0.16}
          phrases={phrases}
          loopPhrases={loopPhrases}
          trackId={selectedSession.id}
          loopTrackId={selectedSession.is_hybrid ? `${selectedSession.id}_loop` : null}
          title={selectedSession.name}
          description={selectedSession.description}
          duration={selectedSession.duration_seconds}
          onClose={() => {
            setIsPlaying(false);
            setIntroEnded(false);
            setSelectedSession(null);
          }}
        />
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E14] via-[#0E1320] to-[#0A0E14]">
        <div className="px-6 pt-12 pb-6">
          <button
            onClick={() => { stopEdgeAudio(); setSelectedSession(null); }}
            className="flex items-center gap-2 text-white/70 hover:text-white"
            data-testid="back-from-session"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Library</span>
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-8 py-8">
          <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${selectedSession.color} 
                         flex items-center justify-center mb-6 ${isPlaying ? 'animate-pulse' : ''}`}>
            <Icon className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2 text-center" style={{ letterSpacing: '-0.01em' }}>{selectedSession.name}</h2>
          <p className="text-white/60 text-center mb-2 max-w-sm">{selectedSession.description}</p>
          <div className="flex items-center gap-2 text-white/40 text-sm mb-8">
            <Clock className="w-4 h-4" />
            <span>{selectedSession.duration_minutes} minutes</span>
          </div>

          {!isPlaying && selectedSession.sections && (
            <div className="w-full max-w-sm space-y-2 mb-8">
              {selectedSession.sections.map((section, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/60">
                    {i + 1}
                  </div>
                  <span className="text-white/70 text-sm capitalize">{section.type}</span>
                </div>
              ))}
            </div>
          )}

          {isPlaying && (
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full border-4 border-white/20 border-t-amber-500 animate-spin mx-auto mb-4" />
              <p className="text-white/60">{introEnded ? 'Identity loop active...' : 'Session in progress...'}</p>
            </div>
          )}

          {selectedSession.coming_soon && (
            <div className="text-center bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 max-w-sm">
              <p className="text-amber-400 text-sm font-medium">Recording soon</p>
              <p className="text-white/50 text-xs mt-1">Audio for this ritual is being added shortly.</p>
            </div>
          )}
        </div>

        <div className="px-6 pb-12">
          {!isPlaying ? (
            <button
              onClick={startSession}
              disabled={selectedSession.coming_soon}
              className={`w-full py-4 rounded-full bg-gradient-to-r ${selectedSession.color} 
                       text-white font-medium flex items-center justify-center gap-2
                       hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid="begin-session-btn"
            >
              <Play className="w-5 h-5" />
              Begin Session
            </button>
          ) : (
            <button
              onClick={completeSession}
              className="w-full py-4 rounded-full bg-white/10 border border-white/20 text-white font-medium hover:bg-white/20"
              data-testid="complete-session-btn"
            >
              Complete Session
            </button>
          )}
        </div>

        {/* Hidden audio elements drive playback */}
        <audio ref={audioRef} preload="auto" playsInline onEnded={handleIntroEnded} />
        <audio ref={loopAudioRef} preload="auto" playsInline />
      </div>
    );
  }

  // Library list view
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E14] via-[#0D131F] to-[#0A0E14]">
      <div className="px-6 pt-12 pb-6">
        <button
          onClick={() => navigate('/networker')}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-6"
          data-testid="back-to-networker"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        <p className="text-[10px] uppercase tracking-[0.4em] text-amber-500/80 mb-2">The Edge</p>
        <h1 className="text-2xl font-semibold text-white mb-1.5" style={{ letterSpacing: '-0.01em' }}>Guided Performance</h1>
        <p className="text-slate-400 text-sm">Start with Daily Identity. Use resets throughout your day.</p>
      </div>

      <div className="px-6 pb-24 space-y-8">
        {Object.entries(categories).map(([category, categorySessions]) => (
          <div key={category}>
            <h2 className="text-base font-medium text-white/85 mb-3 flex items-center gap-2">
              {categoryLabels[category] || category}
              {category === 'edge' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 uppercase tracking-wider">New</span>
              )}
            </h2>
            <div className="space-y-3">
              {categorySessions.map((session) => {
                const Icon = iconMap[session.icon] || Headphones;
                return (
                  <button
                    key={session.id}
                    onClick={() => setSelectedSession(session)}
                    className="w-full bg-white/[0.04] backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/[0.07] transition-all text-left group"
                    data-testid={`audio-session-${session.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${session.color} 
                                    flex items-center justify-center flex-shrink-0
                                    group-hover:scale-105 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white truncate">{session.name}</h3>
                        <p className="text-sm text-slate-400 truncate">{session.description}</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{session.duration_minutes}m</span>
                        <ChevronRight className="w-4 h-4 group-hover:text-white transition-colors" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NetworkerAudioLibraryPage;
